import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import uuid from 'uuid/v4';
import { useQuery } from '@apollo/client';

import Modal from '../../Modal/Modal';
import DragAndDropContainer from './DragAndDropContainer';
import Notification from '../../Notification';
import items, { allItems } from './ItemsModules/items';
import getPostBySlug from '../../../api/graphql/queries/getPostBySlug';
import postEditPost from '../../../api/graphql/mutations/postEditPost';
import SchedulePostConfig from '../StructureComponents/SchedulePostConfig';
import {
  transformCarouselToDnD,
  transformCarouselFromDnD,
  transformFilterToDnD,
  transformFilterFromDnD,
  transformTagsRowtoDnD,
} from '../../../utils/dragAndDrop';
import HeaderLine from '../../HeaderLine';
import { GET_PAGE_LAYOUT } from '../StructureComponents/Config';

import { transformTimesStampToIsoDate } from '../../../utils/postDates';

import { PostInfoContext } from '../../../utils/PostInfoProvider';
import { applyRequiresLogin, itemRequiresLogin } from '../../../utils/requiresLoginFlag';

import OnlineUsers from '../../OnlineUsers';

const isJsonString = (str) => {
  try {
      JSON.parse(str);
  } catch (e) {
      return false;
  }
  return true;
}

const initialPostState = {
  slugName: '',
  history: '',
  name: '',
  postCategory: [],
  type: 'route',
  postDescription: {
    title: {
      en: '',
    },
    subtitle: {
      en: '',
    },
    description: {
      en: '',
    },
    image: {
      en: '',
    },
    tags: [],
    url: '',
    date: new Date(),
    expirationDate: null,
    overlayImage: null,
    cta: {
      url: '',
      text: {
        en: '',
      },
      isDisabled: false,
    },
  },
  requiresCompleteRegistration: false,
  active: false,
  position: 0,
};

const REUSABLE_COMPONENT = 'ReusableComponent';
const COLLAPSIBLE_PAGE = 'CollapsiblePage';
const NO_HEADER_NO_FOOTER = 'no-head-no-foot';

const getComponentsArray = (selectedComponents) => {
  const allMountableComponents = allItems.map(i => i.content);
  const componentsArray = selectedComponents.map((component) => {
    let modifiedCompWithCompId = { ...component };

    if (!component.component && !allMountableComponents.includes(component.content)) {
      const comp = { key: component.content, compId: `${component.type}_${uuid()}` };
      return applyRequiresLogin(comp, component.requiresLogin);
    }
    if (!component.component) {
      modifiedCompWithCompId = {
        component: {
          type: component.content,
          compId: `${component.content}_${uuid()}`,
          placeholder: true,
        },
      };
    } else if (!component.component.compId) {
      modifiedCompWithCompId.component.compId = `${component.component.type}_${uuid()}`;
    }

    if (component.content === 'Carousel') return applyRequiresLogin(transformCarouselFromDnD(modifiedCompWithCompId), component.requiresLogin);
    if (component.content === 'FilterContainer') return applyRequiresLogin(transformFilterFromDnD(modifiedCompWithCompId), component.requiresLogin);
    return applyRequiresLogin(modifiedCompWithCompId.component, component.requiresLogin);
  });

  return componentsArray || [];
};

const getTemplateType = (post) => {
  const componentsArrayFromResponse = JSON.parse(post.components);
  if (post.type === 'component') return REUSABLE_COMPONENT;
  if (componentsArrayFromResponse?.[0].type === 'Body') return NO_HEADER_NO_FOOTER;
  const body = componentsArrayFromResponse.find(el => el.type === 'Body');
  return body?.components[0]?.type;
};

const PostContainer = () => {
  const { postCategory, slugName } = useParams();
  const [postInfo, setPostInfo] = useState(initialPostState);
  const [selectedOnDragAndDrop, setSelectedOnDragAndDrop] = useState([]); // state to render drag and drop items already uploaded to db
  const [toggleRefresh, setRefetch] = useState(false);
  const [status, setStatus] = useState({});
  const [isPublished, setIsPublished] = useState();
  const [scheduledPublishDate, setScheduledPublishDate] = useState();
  const [templateType, setTemplateType] = useState();
  const [isModalOn, setModalState] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(slugName);

  const { data: layoutData } = useQuery(GET_PAGE_LAYOUT, { variables: { layout: templateType }, skip: !templateType });

  const isAContainer = layoutData?.getPageLayout?.isAContainer;
  const isReusableComponent = templateType === REUSABLE_COMPONENT;
  const isCollapsiblePage = templateType === COLLAPSIBLE_PAGE;

  const context = useContext(PostInfoContext);

  const toggleModal = () => {
    setModalState(!isModalOn);
  };

  const addStatusMessage = (icon, text, color) => {
    setStatus({ icon, text, color });
    const timeOutId = setTimeout(() => {
      setStatus({ icon: '', text: '', color: '' });
    }, 3000);
  };

  const getPost = async (slug) => {
    try {
      const res = await getPostBySlug(slug);
      const { post } = res;
      // handle post active and allowed roles in case the initial query brings null on them
      const postActive = post.active === null ? false : post.active;
      const allowedRolesTrick = post.rolesAllowed === null ? [] : post.rolesAllowed;
      post.active = postActive;
      post.rolesAllowed = allowedRolesTrick;
      setIsPublished(postActive);
      setScheduledPublishDate(Number(post.scheduledPublishDate) ? new Date(Number(post.scheduledPublishDate)).toISOString() : null);
      setPostInfo(post);
      // NOTE: don't know what this is
      context.updatePostCategory(post);
      const postTemplate = getTemplateType(post);
      setTemplateType(postTemplate);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (slugName) getPost(slugName);
  }, []);

  useEffect(() => {
    if (!templateType && !postInfo?.components) return;
    const isJson = isJsonString(postInfo.components)
    const componentsArrayFromResponse = isJson ? JSON.parse(postInfo.components) : postInfo.components;
    const body = componentsArrayFromResponse.find(el => el.type === 'Body');
    const components = body?.components || componentsArrayFromResponse[0]?.components || componentsArrayFromResponse;

    const componentsArray = (isAContainer && !isReusableComponent) ? components[0].components : components;

    const arr = [];
    //  const componentsArray = JSON.parse(res.post.components);
    componentsArray?.map(async (comp) => {
      const readableNameMappedFromItems = items.filter(i => i.content === comp.type)[0];
      const readableName = readableNameMappedFromItems ? readableNameMappedFromItems.readableName : comp.type;
      // create objects from db and added them to the state
      const key = uuid();

      // // create the objects to set the state of the drag and drop
      // // we need to pass an id to make every module unique
      let selectedObj = {};
      if (Object.prototype.hasOwnProperty.call(comp, 'key')) {
        selectedObj = {
          content: comp.key,
          id: key,
          type: 'reusableItem',
          readableName: `Reusable item ${comp.key}`,
        };
      } else {
        if (comp.type === 'Carousel') {
          selectedObj = transformCarouselToDnD(comp);
          if (Object.prototype.hasOwnProperty.call(comp, 'areSlidesShort')) {
            selectedObj.component.areSlidesShort = comp.areSlidesShort;
          }
        } else if (comp.type === 'FilterContainer') {
          selectedObj = transformFilterToDnD(comp);
        } else if (comp.type === 'TagsRow') {
          selectedObj = transformTagsRowtoDnD(comp);
        } else {
          selectedObj = {
            content: comp.type,
            id: key,
            component: comp,
            readableName,
            placeholder: comp.placeholder ? comp.placeholder : false,
          };
        }
      }
      // "Requires user login" lives on the wrapper item so the per-type forms
      // (which rebuild item.component from their own state) can't drop it.
      if (itemRequiresLogin(comp)) selectedObj.requiresLogin = true;
      arr.push(selectedObj);
    });

    handlerSetSelectedOnDragAndDrop(arr);
  }, [layoutData, postInfo]);

  // postInfo is updated if isPublished changes (active)
  useEffect(() => {
    if (isPublished !== undefined) {
      setPostInfo({ ...postInfo, active: isPublished });
    }
  }, [isPublished]);

  useEffect(() => {
    const { postDescription: { url } = null } = postInfo || {};
    const rootWithoutSlash = process.env.PUBLIC_ROUTE.replace(/\/+$/, '');
    setPreviewUrl(url ? `${rootWithoutSlash}${url}` : `${rootWithoutSlash}/${slugName}`);
  }, [slugName, postInfo]);

  const toggleRefetch = () => {
    setRefetch(!toggleRefresh);
    addStatusMessage('icon-checkbox', 'Post updated', 'green');
  };

  useEffect(() => {
    setIsPublished(postInfo.active);
  }, [postInfo.active]);

  const parsePostData = (post, selectedComp) => {
    const newPost = post || postInfo;
    const { postDescription } = { ...newPost };
    postDescription.date = transformTimesStampToIsoDate(postDescription.date);
    postDescription.launcherSortDate = transformTimesStampToIsoDate(postDescription.launcherSortDate);
    postDescription.expirationDate = transformTimesStampToIsoDate(postDescription.expirationDate);

    const postComponents = newPost.components;
    if (!postComponents) return newPost;

    // deep cloning state to avoid mutation
    const newSelectedArray = JSON.parse(JSON.stringify(selectedComp || selectedOnDragAndDrop));
    const componentsArray = getComponentsArray(newSelectedArray);
    const componentsArrayFromResponse = Array.isArray(postComponents) ? postComponents : JSON.parse(postComponents);

    const body = componentsArrayFromResponse?.find(el => el.type === 'Body');

    const components = body?.components || componentsArrayFromResponse;
    // NOTE: if the template is a contanier or component, we DON'T add the components directly to the body
    if (isAContainer || isReusableComponent) components[0].components = componentsArray;
    else if (body) body.components = componentsArray;

    newPost.components = String(JSON.stringify(componentsArrayFromResponse));

    return newPost;
  };

  const togglePublish = () => {
    const newPost = { ...postInfo };
    newPost.active = !newPost.active;
    newPost.scheduledPublishDate = null;

    return parsePostData(newPost, selectedOnDragAndDrop);
  };

  const handlePublishDate = () => {
    const newPost = { ...postInfo };
    newPost.active = false;
    newPost.scheduledPublishDate = scheduledPublishDate;

    return parsePostData(newPost, selectedOnDragAndDrop);
  };

  const publishPostHandler = () => {
    setScheduledPublishDate(null);

    const post = togglePublish();

    postEditPost(post)
      .then((res) => {
        if (res?.isActive) addStatusMessage('icon-checkbox', 'Post Published', 'green');
        else addStatusMessage('icon-checkbox', 'Post Unpublished', 'green');
        setIsPublished(res.isActive);
      })
      .catch(err => console.log(err));
  };

  // Schedule publish date
  const saveScheduledPublishDate = () => {
    setIsPublished(false);
    const post = handlePublishDate();

    postEditPost(post)
      .then((res) => {
        if (res?.isEdited) {
          if (post.scheduledPublishDate) addStatusMessage('icon-checkbox', 'Post Scheduled', 'green');
          else addStatusMessage('icon-checkbox', 'Post Unscheduled', 'green');
        }
      })
      .catch(err => console.log(err));
  };

  const handlerSetSelectedOnDragAndDrop = (comp) => {
    setSelectedOnDragAndDrop(comp);
    setPostInfo(parsePostData(postInfo, comp));
  };

  const sendPost = () => {
    console.log('saving');
    postEditPost(parsePostData())
      .then((res) => {
        if (res) {
          const { isEdited = false } = res;
          if (isEdited) {
            addStatusMessage('icon-checkbox', 'Post updated', 'green');
          }
        }
      })
      .catch(err => console.log('There is an error in postContainer: ', err));
  };

  useEffect(() => {
    if (slugName) {
      console.log('GOING TO REFETCH, WHY?');
      getPost(slugName);
    }
  }, [toggleRefresh]);

  return (
    <div>
      <HeaderLine postCategory={postCategory === 'press-area' ? 'black' : postCategory} />
      <div className="flex justify-between mb-2 items-center">
        <div className="ml-8">{status.text && <Notification {...status} />}</div>
        <div className="flex ml-10">
          <OnlineUsers slugName={slugName} />
          <button className="font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase bg-greyFilter h-12" type="button" onClick={sendPost}>
            save post
          </button>
          <a href={`${previewUrl}?isPreviewFromAdmin=true`} target="_blank" rel="noopener noreferrer">
            <button className="font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase bg-greyFilter flex items-center mx-2  h-12" type="button">
              page preview
              {' '}
              <span className="font-icon icon-eye text-black text-lg pl-2 font-bold" />
            </button>
          </a>
          <button onClick={publishPostHandler} className={`font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase h-12 ${isPublished ? 'bg-greyFilter' : ' bg-green-pin'}`} type="button">
            {isPublished ? 'Unpublish' : 'Publish'}
          </button>
          <button
            onClick={() => {
              toggleModal();
            }}
            className={`font-americaMonoBold text-xs px-6 py-2 mx-2 rounded-full uppercase h-12 ${scheduledPublishDate ? 'bg-orange-bg' : 'bg-greyFilter'}`}
            type="button"
          >
            <span className="font-icon icon-clock" />
          </button>
        </div>
      </div>
      <DragAndDropContainer
        templateType={templateType}
        addStatusMessage={addStatusMessage}
        toggleRefetch={toggleRefetch}
        post={postInfo}
        updatePost={setPostInfo}
        handlerSetSelectedOnDragAndDrop={handlerSetSelectedOnDragAndDrop}
        selectedOnDragAndDrop={selectedOnDragAndDrop}
        sendPost={sendPost}
        isCollapsible={isCollapsiblePage}
      />
      {isModalOn && (
        <Modal handleClick={toggleModal} size="w-550px xxl1:94%" background="pb-4 px-0 scrollbar__grey">
          <div>
            <SchedulePostConfig
              closeModal={toggleModal}
              scheduledPublishDate={scheduledPublishDate}
              setScheduledPublishDate={setScheduledPublishDate}
              saveScheduledPublishDate={saveScheduledPublishDate}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PostContainer;
